import React from 'react'
import i18next from 'i18next'
import { FormInputProps } from '@microservices/wiskey-react-components'
import { ReportProblemOutlined } from '@mui/icons-material'
import { Box, Chip, Tooltip } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { ColumnDef } from '@tanstack/react-table'

import {
  ConfigRowsType,
  GETAction,
  GETDictionary,
  GETEntityColumn,
  GETField,
} from '../types'

import { GENERATOR_INPUT_TYPE, OBJECT_VALUE_TYPE } from './forms'
import { ACTION_CODE, EVENT_CODE } from './modals'

export const SIZE_PAGE = '20'
export const VIEW_ROWS_AMOUNT = 10
export const EMBEDDED_VIEW_ROWS_AMOUNT = 5

export enum TABLE_DESCRIPTION {
  MODIFY = 'modify',
  COMMON = 'common',
}

export enum FIELD_BIND_TYPE {
  FIELD = 'field',
  JS = 'js',
  STATIC = 'static',
}

export enum FIELD_BIND_TYPE_ENUM {
  FIELD = 'FIELD',
  JS = 'JS',
  STATIC = 'STATIC',
}

export enum BIND_TYPE {
  ACTION = 'action',
  FIELD = 'field',
  JSX = 'jsx',
  JS = 'js',
}

export enum GANTT_ENTITY_VARIABLE_TYPE {
  ACTION = 'action',
  TOOLTIP = 'tooltip',
  CONTEXT_MENU = 'context_menu',
}

export enum GANTT_BIND_TYPE {
  FIELD = 'field',
  JS = 'js',
  STATIC = 'static',
}

export enum GANTT_ICON_BIND_TYPE {
  ICON = 'icon',
  JSX = 'jsx',
}

export enum UPDATE_TYPE {
  MANUAL = 'MANUAL',
  WEBSOCKET = 'WEBSOCKET',
  INTERVAL = 'INTERVAL',
}

export const PREVIEW_COLUMNS_FIELD_TYPES = {
  EXAMPLE: 'example',
  TEXT_FORMAT: 'textFormat',
  TEXT_COLOR: 'textColor',
  BACKGROUND: 'background',
  BORDER: 'border',
}

export const VIEW_COLUMNS: ColumnDef<GETEntityColumn>[] = [
  {
    accessorKey: 'code',
    header: i18next.t('viewColumnsTable.id'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title',
    header: i18next.t('viewColumnsTable.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'bindType',
    header: i18next.t('viewColumnsTable.bindType'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'value',
    header: i18next.t('viewColumnsTable.value'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'valueType',
    header: i18next.t('viewColumnsTable.valueType'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'columnToSort',
    header: i18next.t('viewColumnsTable.columnToSort'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'pinnedColumn',
    header: i18next.t('viewColumnsTable.pinnedColumn'),
    meta: { flex: 1 },
  },
]

export const VIEW_ACTION_COLUMNS: ColumnDef<GETAction>[] = [
  {
    accessorKey: 'code',
    header: i18next.t('viewActionsTable.id'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title',
    header: i18next.t('viewActionsTable.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'eventCode',
    header: i18next.t('viewActionsTable.eventCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'actionCode',
    header: i18next.t('viewActionsTable.actionCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'params',
    header: i18next.t('viewActionsTable.params'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'objectFormTitle',
    header: i18next.t('viewActionsTable.objectFormCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'contextMenuTitle',
    header: i18next.t('viewActionsTable.contextMenu'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'objectTooltipTitle',
    header: i18next.t('viewActionsTable.tooltip'),
    meta: { flex: 1 },
  },
  {
    id: 'target',
    accessorFn: row => row.target?.title?.trim() || '',
    header: i18next.t('viewActionsTable.targetColumn'),
    meta: { flex: 1 },
  },
]

export const FORM_ACTION_COLUMNS: ColumnDef<GETAction>[] = [
  {
    accessorKey: 'code',
    header: i18next.t('viewActionsTable.id'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title',
    header: i18next.t('viewActionsTable.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'eventCode',
    header: i18next.t('viewActionsTable.eventCode'),
    meta: { flex: 1 },
    cell: props => {
      const eventCode = props.getValue() as EVENT_CODE | null
      const action = props.row.original

      const chips: JSX.Element[] = []

      if (eventCode === EVENT_CODE.CHANGE_FIELD_VALUE && action.objectFieldTitle) {
        chips.push(
          <Chip
            key='event-field'
            color='primary'
            label={action.objectFieldTitle}
            size='small'
            variant='outlined'
          />
        )
      }

      if (eventCode === EVENT_CODE.ON_VARIABLE_CHANGE && action.variable) {
        chips.push(
          <Chip
            key='event-variable'
            color='secondary'
            label={action.variable.title ?? action.variable.internalId}
            size='small'
            variant='outlined'
          />
        )
      }

      return (
        <Box alignItems='center' display='flex' gap={0.5} justifyContent='space-between'>
          <Box
            sx={{
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {eventCode}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>{chips}</Box>
        </Box>
      )
    },
  },
  {
    accessorKey: 'actionCode',
    header: i18next.t('viewActionsTable.actionCode'),
    meta: { flex: 1 },
    cell: props => {
      const actionCode = props.getValue() as ACTION_CODE | null
      const action = props.row.original

      const chips: JSX.Element[] = []

      if (actionCode === ACTION_CODE.SET_FIELD_VALUE && action.objectFieldTitle) {
        chips.push(
          <Chip
            key='action-field'
            color='primary'
            label={action.objectFieldTitle}
            size='small'
            variant='outlined'
          />
        )
      }

      if (actionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE && action.variable) {
        chips.push(
          <Chip
            key='action-variable'
            color='secondary'
            label={action.variable.title ?? action.variable.internalId}
            size='small'
            variant='outlined'
          />
        )
      }

      return (
        <Box alignItems='center' display='flex' gap={0.5} justifyContent='space-between'>
          <Box
            sx={{
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {actionCode}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>{chips}</Box>
        </Box>
      )
    },
  },
  {
    accessorKey: 'params',
    header: i18next.t('viewActionsTable.params'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'objectFormTitle',
    header: i18next.t('viewActionsTable.objectFormCode'),
    meta: { flex: 1 },
  },
]

export const CONFIG_COLUMNS: GridColDef<ConfigRowsType>[] = [
  {
    field: 'id',
    headerName: i18next.t('configColumnsTable.id'),
  },
  {
    field: 'code',
    headerName: i18next.t('configColumnsTable.code'),
    flex: 1,
  },
  {
    field: 'title',
    headerName: i18next.t('configColumnsTable.title'),
    flex: 1,
  },
  {
    field: 'objectTitle',
    headerName: i18next.t('configColumnsTable.objectTitle'),
    flex: 1,
  },
]

export const CONFIG_CONTROL_PANELS_COLUMNS: GridColDef<ConfigRowsType>[] = [
  { field: 'id', headerName: i18next.t('configColumnsTable.id') },
  {
    field: 'title',
    headerName: i18next.t('configColumnsTable.title'),
    flex: 1,
  },
]

export const CONFIG_WINDOWS_COLUMNS: GridColDef<ConfigRowsType>[] = [
  { field: 'internalId', headerName: i18next.t('configColumnsTable.code') },
  {
    field: 'title',
    headerName: i18next.t('configColumnsTable.title'),
    flex: 1,
  },
]

export const FORM_COLUMNS: ColumnDef<GETField>[] = [
  {
    accessorKey: 'code',
    header: i18next.t('formColumnsTable.code'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title',
    header: i18next.t('formColumnsTable.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'bindType',
    header: i18next.t('formColumnsTable.bindType'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'value',
    header: i18next.t('formColumnsTable.value'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'valueType',
    header: i18next.t('formColumnsTable.valueType'),
    meta: { flex: 1 },
    cell: ({ row }) => {
      const { pathArray, valueType } = row.original
      const hasPathArrayError =
        !!valueType &&
        OBJECT_VALUE_TYPE.includes(valueType) &&
        (!pathArray || pathArray.length === 0)

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <Box
            component='span'
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
          >
            {valueType}
          </Box>
          {hasPathArrayError && (
            <Tooltip title='Path Array is missing in Displayed Field'>
              <ReportProblemOutlined
                sx={{
                  color: theme => theme.palette.color.warning,
                  fontSize: '1.3rem',
                  flexShrink: 0,
                  ml: 0.5,
                }}
              />
            </Tooltip>
          )}
        </Box>
      )
    },
  },
]

export const VIEW_OR_FORM_CREATE_INPUTS = ({
  bindingValues = [],
  bindingValuesTypes = [],
}: {
  bindingValues?: GETDictionary[]
  bindingValuesTypes?: GETDictionary[]
}): FormInputProps[] => [
  {
    name: 'rowBindingType',
    inputType: GENERATOR_INPUT_TYPE.SELECT,
    placeholder: i18next.t('placeholder.bindType'),
    label: 'Binding',
    selectOptions: bindingValues?.map(cat => ({ id: cat.id, name: cat.code })),
    rules: { required: true, validate: value => value !== i18next.t('placeholder.bindType') },
  },
  {
    name: 'rowBindingValueTypeCode',
    inputType: GENERATOR_INPUT_TYPE.SELECT,
    placeholder: i18next.t('placeholder.valueType'),
    label: 'Value Type',
    selectOptions: bindingValuesTypes?.map(cat => ({ id: cat.id, name: cat.code })),
    rules: { required: true, validate: value => value !== i18next.t('placeholder.valueType') },
  },
  {
    name: 'rowBindingValue',
    inputType: GENERATOR_INPUT_TYPE.INPUT,
    placeholder: i18next.t('placeholder.value'),
    label: 'Value',
    rules: { validate: value => value.trim().length !== 0 },
  },
]

export const DICTIONARY_TABLE_COLUMNS: GridColDef[] = [
  {
    field: 'id',
    headerName: i18next.t('dictionaryTable.id'),
  },
  {
    field: 'code',
    headerName: i18next.t('dictionaryTable.code'),
    width: 300,
  },
  {
    field: 'title',
    headerName: i18next.t('dictionaryTable.title'),
    flex: 1,
  },
  {
    field: 'categoryTitle',
    headerName: i18next.t('dictionaryTable.categoryTitle'),
    flex: 1,
  },
]

export const OBJECT_FIELD_CHANGES_COLUMNS: GridColDef[] = [
  {
    field: 'changeDate',
    headerName: i18next.t('objectFieldChangesColumnsTable.changeDate'),
    width: 100,
  },
  {
    field: 'author',
    headerName: i18next.t('objectFieldChangesColumnsTable.author'),
    width: 100,
  },
  {
    field: 'field',
    headerName: i18next.t('objectFieldChangesColumnsTable.field'),
    width: 100,
  },
  {
    field: 'newValue',
    headerName: i18next.t('objectFieldChangesColumnsTable.newValue'),
    width: 100,
  },
  {
    field: 'oldValue',
    headerName: i18next.t('objectFieldChangesColumnsTable.oldValue'),
    width: 100,
  },
]

export const SEARCH_ASSISTANT_TABLE_COLUMNS: GridColDef[] = [
  { field: 'id', headerName: i18next.t('searchAssistantTable.id'), flex: 1, minWidth: 50 },
  {
    field: 'code',
    headerName: i18next.t('searchAssistantTable.internalID'),
    flex: 1,
    minWidth: 170,
  },
  { field: 'title', headerName: i18next.t('searchAssistantTable.title'), flex: 1, minWidth: 170 },
  {
    field: 'objectTitle',
    headerName: i18next.t('searchAssistantTable.object'),
    flex: 1,
    minWidth: 170,
  },
  // {
  //   field: 'valueParams',
  //   headerName: i18next.t('searchAssistantTable.value_params'),
  //   flex: 1,
  //   minWidth: 170,
  // },
  // {
  //   field: 'value',
  //   headerName: i18next.t('searchAssistantTable.value'),
  //   flex: 1,
  //   minWidth: 170,
  //   sortable: false,
  //   valueFormatter: params =>
  //     params.value.map((value: GETCriteriaValueType) => value.name).join(', '),
  // },
  // {
  //   field: 'fieldType',
  //   headerName: i18next.t('searchAssistantTable.field_type'),
  //   flex: 1,
  //   minWidth: 170,
  // },
  // {
  //   field: 'objectValue',
  //   headerName: i18next.t('searchAssistantTable.objectValue'),
  //   flex: 1,
  //   minWidth: 170,
  // },
]

export const CONTEXTS_COLUMNS: GridColDef<ConfigRowsType>[] = [
  {
    field: 'title',
    headerName: i18next.t('configColumnsTable.title'),
    flex: 1,
  },
]

export const CONFIG_CONTEXT_MENU_COLUMNS: GridColDef<ConfigRowsType>[] = [
  {
    field: 'id',
    headerName: i18next.t('configColumnsTable.id'),
  },
  {
    field: 'title',
    headerName: i18next.t('configColumnsTable.title'),
    flex: 1,
  },
  {
    field: 'objectTitle',
    headerName: i18next.t('configColumnsTable.objectTitle'),
    flex: 1,
  },
]

export const VARIABLES_COLUMNS: GridColDef[] = [
  {
    field: 'internalId',
    headerName: i18next.t('variableTable.internalId'),
    flex: 1,
  },
  {
    field: 'title',
    headerName: i18next.t('variableTable.title'),
    flex: 1,
  },
  {
    field: 'variableType',
    headerName: i18next.t('variableTable.variableType'),
    flex: 1,
  },
  {
    field: 'valueType',
    headerName: i18next.t('variableTable.valueType'),
    flex: 1,
  },
]

export const STYLE_SETTINGS_COLUMNS: GridColDef<ConfigRowsType>[] = [
  {
    field: 'code',
    headerName: 'Internal ID',
    flex: 1,
    headerAlign: 'center',
  },
  {
    field: 'title',
    headerName: i18next.t('configColumnsTable.title'),
    flex: 1,
    headerAlign: 'center',
  },
]

export const DISPLAY_RULE_COLUMNS: GridColDef<ConfigRowsType>[] = [
  {
    field: 'code',
    headerName: i18next.t('displayRulesColumnsTables.code'),
    flex: 1,
  },
  {
    field: 'title',
    headerName: i18next.t('displayRulesColumnsTables.title'),
    flex: 1,
  },
  {
    field: 'objectTitle',
    headerName: i18next.t('displayRulesColumnsTables.objectTitle'),
    flex: 1,
  },
]

export const SEARCH_ASSISTANTS_COLUMNS: GridColDef<ConfigRowsType>[] = [
  {
    field: 'internalId',
    headerName: i18next.t('searchAssistantsColumnsTables.code'),
    flex: 1,
  },
  {
    field: 'title',
    headerName: i18next.t('searchAssistantsColumnsTables.title'),
    flex: 1,
  },
]

export const CONFIG_TOOLTIPS_COLUMNS: GridColDef<ConfigRowsType>[] = [
  {
    field: 'id',
    headerName: i18next.t('tooltips.allTooltipsTable.id'),
  },
  {
    field: 'code',
    headerName: i18next.t('tooltips.allTooltipsTable.internalId'),
    flex: 1,
  },
  {
    field: 'title',
    headerName: i18next.t('tooltips.allTooltipsTable.title'),
    flex: 1,
  },
  {
    field: 'objectTitle',
    headerName: i18next.t('tooltips.allTooltipsTable.objectName'),
    flex: 1,
  },
]

export const TOOLTIP_POINT_COLUMNS = [
  {
    accessorKey: 'title',
    header: i18next.t('tooltips.tooltipPointsTable.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'bindType',
    header: i18next.t('tooltips.tooltipPointsTable.binding'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'value',
    header: i18next.t('tooltips.tooltipPointsTable.value'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'valueType',
    header: i18next.t('tooltips.tooltipPointsTable.valueType'),
    meta: { flex: 1 },
  },
]

export const OBJECT_STATE_VALUES_COLUMNS = [
  {
    accessorKey: 'fieldValue',
    header: i18next.t('displayRulesValueColumns.fieldValue'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'styleSettingsTitle',
    header: i18next.t('displayRulesValueColumns.styleSettings'),
    meta: { flex: 1 },
  },
]

export const LIST_CONTROL_COLUMNS = VIEW_COLUMNS

export const LIST_CONTROL_ACTION_COLUMNS = VIEW_ACTION_COLUMNS

export const CONTEXT_MENU_POINT_ACTION_COLUMNS: ColumnDef<GETAction>[] = [
  {
    accessorKey: 'code',
    header: i18next.t('viewActionsTable.id'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title',
    header: i18next.t('viewActionsTable.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'eventCode',
    header: i18next.t('viewActionsTable.eventCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'actionCode',
    header: i18next.t('viewActionsTable.actionCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'objectFormTitle',
    header: i18next.t('viewActionsTable.objectFormCode'),
    meta: { flex: 1 },
  },
]
